import React, { ComponentType } from 'react';
import { ColorGradient } from '../GradientProp';
import { Camera2D, Camera3D, Checkbox, ColorPicker, NumberSlider, PropComponentProps } from '../propComponents';
import { TexturePicker } from '../TextureProp';

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

// A type from a glsl source
export class GLSLType {
  // name of the type (ex: "vec3", "float", etc)
  name: string;
  // null if the type isn't an array, or the length expression if it is an array
  array: null | string;

  constructor(name: string, array: null | string) {
    this.name = name;
    this.array = array;
  }

  // Emit the type as a string
  asGLSLStr(): string {
    return `${this.name}${this.array !== null ? `[${this.array}]` : ""}`;
  }

  // Return the vector dimension (or 1 if not a vector) of this type
  vectorDim(): number {
    const vecTypes: {[type: string]: number} = {"vec2": 2, "vec3": 3, "vec4": 4};
    if(this.name in vecTypes) 
      return vecTypes[this.name];
    else
      return 1;
  }

  toString(): string {
    return `${this.name}${this.array === null ? '' : `[${this.array}]`}`;
  }
}

// A glsl const/uniform declaration
export class GLSLProp {
  isConst: boolean;
  type: GLSLType;
  name: string;

  constructor(isConst: boolean, type: GLSLType, name: string) {
    this.isConst = isConst;
    this.type = type;
    this.name = name;
  }

  // generate a const/uniform declaration of this variable
  toGLSLDecl(forceConst: boolean, defaultValue: string): string {
    let res = "";
    if (this.isConst || forceConst) {
      res += "const";
    } else {
      res += "uniform";
    }
    res += ` ${this.type.asGLSLStr()} ${this.name}`;
    if(this.isConst || forceConst) {
      res += ` = ${defaultValue}`;
    }
    res += `;\n`;

    return res;
  }
}

// A map from glsl uniform name -> uniform location
export interface GLSLUniformLocations {
  [varName: string]: WebGLUniformLocation | null;
}

// A property from a source file. Properties are either uniforms or consts.
// Const properties are set at compile time, while unifroms are passed to the shader at runtime.
// Some properties have to be consts (because glsl only allows const values in certain contexts).
// Other properties can be emitted as uniform (to allow interactive changes) or can be emitted as const (to improve shader performance).
// Some properies have multiple glsl variables backing them (such as a camera or color palette).

// Property does not hold the actual value of the property. Rather, it defines a transformation: glsl literals <-> js values
export abstract class Property {
  // The name of the property displayed to the user
  displayName: string = "";
  // The backing glsl variables
  vars: GLSLProp[] = [];

  // If the display of the variable is conditional on another property being set to true.
  // If so, this is the index of that property in this shader.
  conditionalPropIndex: number | null = null;

  // Emit a glsl declaration for the consts/uniforms set to value. If forceConst is true, all vars will be emitted as const. Otherwise, vars will match how they are declared in the source file.
  abstract toGLSLDecl(val: PropertyValue): string;
  
  // Set the appropriate glsl uniforms to value.
  abstract setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations): void;

  // Check if changing this property from curVal to newVal will require glsl code to be re-emitted and compiled.
  // Chaning consts will require re-compilation, changing uniforms won't
  changeRequiresCompile(curVal: PropertyValue, newVal: PropertyValue): boolean {
    // if any vars are const recompile is required
    for(let i = 0; i < this.vars.length; i++) {
      if(this.vars[i].isConst) return true;
    }
    // if forceConst is true or is changing recompile is required
    if(newVal.forceConst || curVal.forceConst !== newVal.forceConst) {
      return true;
    }

    // otherwise vars are uniform + are staying uniform, so no recompile needed
    return false;
  }

  // Return true if this property is always const (no matter if the user chooses forceConst or not)
  alwaysConst(): boolean {
    // always const if all variables are const
    for(let i = 0; i < this.vars.length; i++) {
      if(!this.vars[i].isConst) {
        return false;
      }
    }

    return true;
  }

  // Return the react component that allows editing of the prop
  abstract editComponent(): ComponentType<PropComponentProps>

  // Return argument to pass to react component as args prop
  componentArgs(): any {
    return null;
  }

  // Convert a glsl literal to a value that can be passed to toGLSLDecl or setGLSLUniform
  abstract valFromGLSL(literal: string[]): any;

  // Interpolate linearly between left and right values at time t (t in [0, 1]). Used for animation between keyframes.
  abstract interpolate(left: any, right: any, t: number): any;

  // Return true if the property cares about events (keypress + scroll wheel) that occured on the canvas.
  usesCanvasEvents(): boolean { return false; }

  // Called when a key press occured on the canvas and usesCanvasEvents is true. Given the key press and the current value of the property, return the property's new value
  handleKeyPress(event: React.KeyboardEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue { return curVal; }

  // Same as handleKeyPress, but for scroll events
  handleScroll(event: React.WheelEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue { return curVal; }

  // Same as handleKeyPress, but for mouse drag events
  handleMouseDrag(event: React.MouseEvent, curVal: PropertyValue, deltaMousePos: Vec2, aspectRatio: number, dist: number): PropertyValue { return curVal; }

  // --- Helper methods for converting to/from glsl ---
  // Parse a number array from a glsl vec literal
  static vecFromGLSLLiteral(literal: string, size: number): number[] {
    const zeroVec = Array.from({length: size}, (v, i) => 0.0);

    // a glsl vec literal of size n is of the form:
    // vecn(c0, c1, c2, ...)
    if (!literal.startsWith(`vec${size}(`) || !literal.endsWith(")")) return zeroVec;

    const numsStr = literal.replace(`vec${size}(`, "").replace(")", "");
    const nums = numsStr.split(",");
    if (nums.length !== size) return zeroVec;

    return nums.map((str) => +str);
  }

  // Convert a number array to a glsl vec literal
  static vecToGLSLLiteral(val: number[]): string {
    if(val.length === 1) {
      return this.floatToGLSLLiteral(val[0]);
    } else {
      return `vec${val.length}(${val.map((num) => this.floatToGLSLLiteral(num)).join(", ")})`;
    }
  }

  // Convert a 2d number array to a glsl array of vec literals
  static vecArrayToGLSLLiteral(val: number[][], vecSize: number, length: string): string {
    return `vec${vecSize}[${length}](${val.map((vec) => Property.vecToGLSLLiteral(vec)).join(", ")})`;
  }

  // Convert a number array to a glsl float array
  static floatArrayToGLSLLiteral(val: number[], length: string): string {
    return `float[${length}](${val.map((val) => this.floatToGLSLLiteral(val)).join(", ")})`;
  }

  // Parse a floating point value from a glsl float literal
  static floatFromGLSLLiteral(literal: string): number { return +literal; }
  static floatToGLSLLiteral(val: number): string {
    if(Number.isInteger(val)) {
      return val.toString() + ".0";
    } else {
      return val.toString(); 
    }
  }

  // Interpolate linearly between numbers
  static interpolateNumber(left: number, right: number, t: number): number {
    return left * (1.0 - t) + right * t;
  }

  static interpolateVector(left: number[], right: number[], t: number): number[] {
    let res = [];
    for(let i = 0; i < Math.min(left.length, right.length); i++) {
      res.push(this.interpolateNumber(left[i], right[i], t));
    }
    return res;
  }
}

// A set of property values + forceConsts to be applied to Property[]
export interface PropertyValue {
  value: any;
  forceConst: boolean;
}

// A integer slider (bound between lowRange and highRange)
// Or a integer input without bound (null lowRange and highRange)
class PropertyInt extends Property {
  lowRange: number | null;
  highRange: number | null;

  toGLSLDecl(val: PropertyValue): string {
    return this.vars[0].toGLSLDecl(val.forceConst, Math.floor((val.value as number[])[0]).toString());
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst || this.vars[0].isConst) return;

    gl.uniform1i(uniformLocs[this.vars[0].name], (val.value as number[])[0]);
  }

  valFromGLSL(literal: string[]): any {
    return [+literal[0]];
  }

  interpolate(left: any, right: any, t: number): any {
    return Property.interpolateVector(left as number[], right as number[], t).map((val) =>
      Math.round(val)
    );
  }

  editComponent() {
    return NumberSlider;
  }

  componentArgs() {
    if(this.lowRange !== null && this.highRange !== null) {
      return [this.lowRange, this.highRange, 1.0];
    } else {
      return [];
    }
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
    if(args.length === 2) {
      this.lowRange = +args[0];
      this.highRange = +args[1];
    } else {
      this.lowRange = null;
      this.highRange = null;
    }
  }
}

// A floating point slider (bound between lowRange and highRange)
// Or a number input without bounds (null lowRange and highRange)
class PropertyFloat extends Property {
  lowRange: number | null;
  highRange: number | null;

  toGLSLDecl(val: PropertyValue): string {
    const value = val.value as number[];

    let literal;
    if(value.length === 0)
      literal = Property.floatToGLSLLiteral(value[0]);
    else
      literal = Property.vecToGLSLLiteral(value);

    return this.vars[0].toGLSLDecl(val.forceConst, literal);
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst || this.vars[0].isConst) return;

    const value = val.value as number[];
    if(value.length === 1)
      gl.uniform1f(uniformLocs[this.vars[0].name], value[0]);
    else if(value.length === 2)
      gl.uniform2fv(uniformLocs[this.vars[0].name], value);
    else if(value.length === 3)
      gl.uniform3fv(uniformLocs[this.vars[0].name], value);
    else if(value.length === 4)
      gl.uniform4fv(uniformLocs[this.vars[0].name], value);
  }

  valFromGLSL(literal: string[]): any {
    if(this.vars[0].type.vectorDim() === 1)
      return [Property.floatFromGLSLLiteral(literal[0])];
    else
      return Property.vecFromGLSLLiteral(literal[0], this.vars[0].type.vectorDim());
  }

  interpolate(left: any, right: any, t: number): any {
    return Property.interpolateVector(left as number[], right as number[], t);
  }

  editComponent() {
    return NumberSlider;
  }

  componentArgs() {
    if(this.lowRange !== null && this.highRange !== null) {
      return [this.lowRange, this.highRange, (this.highRange - this.lowRange) / 10000.0 ];
    } 
    else {
      return [];
    }
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
    if(args.length === 2) {
      this.lowRange = +args[0];
      this.highRange = +args[1];
    } else {
      this.lowRange = null;
      this.highRange = null;
    }
  }
}

// A color picker for a vec3
class PropertyColor extends Property {
  toGLSLDecl(val: PropertyValue): string {
    return this.vars[0].toGLSLDecl(val.forceConst, Property.vecToGLSLLiteral(val.value as Vec3));
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst || this.vars[0].isConst) return;

    gl.uniform3fv(uniformLocs[this.vars[0].name], (val.value as Vec3));
  }

  valFromGLSL(literal: string[]): any {
    return Property.vecFromGLSLLiteral(literal[0], 3);
  }

  interpolate(left: any, right: any, t: number): any {
    // TODO: interpolate in HSV space (for smoother transition)
    return Property.interpolateVector(left as number[], right as number[], t);
  }

  editComponent() {
    return ColorPicker;
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

export type ColorPaletteEntry = { color: [number, number, number], offset: number };
// A color palette -- A set of colors interpolated together across a range
class PropertyColorPalette extends Property {
  toGLSLDecl(val: PropertyValue): string {
    const value = val.value as ColorPaletteEntry[];
    let res = "";
    // emit const number of entries variable
    res += this.vars[0].toGLSLDecl(true, value.length.toString());
    // emit color array
    const colorsVal = Property.vecArrayToGLSLLiteral(value.map((entry) => entry.color), 3, this.vars[1].type.array!);
    res += this.vars[1].toGLSLDecl(val.forceConst, colorsVal);
    // emit offset array
    const offsetVal = Property.floatArrayToGLSLLiteral(value.map((entry) => entry.offset), this.vars[2].type.array!);
    res += this.vars[2].toGLSLDecl(val.forceConst, offsetVal);

    return res;
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst) return;

    const value = val.value as ColorPaletteEntry[];

    if(!this.vars[0].isConst) {
      gl.uniform1i(uniformLocs[this.vars[0].name], value.length);
    }
    if(!this.vars[1].isConst) {
      // color values need to be flattened
      const flatColors= value.reduce<number[]>((acc, entry) => acc.concat(entry.color), []);
      gl.uniform3fv(uniformLocs[this.vars[1].name], flatColors);
    }
    if(!this.vars[2].isConst) {
      gl.uniform1fv(uniformLocs[this.vars[2].name], value.map((entry) => entry.offset));
    }
  }

  changeRequiresCompile(curVal: PropertyValue, newVal: PropertyValue) {
    // if forceConst is set or changing, recompile needed
    if(newVal.forceConst || curVal.forceConst !== newVal.forceConst) {
      return true;
    }
    // if colors or offset array is cont, an change requires recompile
    if(this.vars[1].isConst || this.vars[2].isConst) return true;

    // if the length var is const, only a change in the number of colors requires recompile
    if(this.vars[0].isConst && (curVal.value as ColorPaletteEntry[]).length !== (newVal.value as ColorPaletteEntry[]).length) return true;

    return false;
  }

  valFromGLSL(literal: string[]): any {
    // TODO: actually parse literal
    return [
      {color: [0.0, 0.0, 0.0], offset: 0.0},
      {color: [1.0, 1.0, 1.0], offset: 1.0},
    ];
  }

  interpolate(left: any, right: any, t: number): any {
    // TODO: interpolate stop positions / colors
    return t < 0.5 ? left : right;
  }

  editComponent() {
    return ColorGradient;
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

// A boolean checkbox
class PropertyCheckbox extends Property {
  valFromGLSL(literal: string[]): any {
    return literal[0] === "true";
  }

  toGLSLDecl(val: PropertyValue): string {
    const literal = (val.value as boolean) ? "true" : "false";
    return this.vars[0].toGLSLDecl(val.forceConst, literal);
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst || this.vars[0].isConst) return;

    gl.uniform1i(uniformLocs[this.vars[0].name], (val.value as boolean) ? 1 : 0);
  }

  interpolate(left: any, right: any, t: number): any {
    return t < 0.5 ? left : right;
  }

  editComponent() { return Checkbox; }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

// a 2d texture that the user can specify an image for
export type TextureType = {
  image: ImageBitmap | null;
};

class PropertyTexture extends Property {
  valFromGLSL(literal: string[]): any {
    return {
      image: null,
    };
  }

  toGLSLDecl(val: PropertyValue): string {
    // force uniform -- sampler2D has to be a uniform
    return this.vars[0].toGLSLDecl(false, "");
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    const value = val.value as TextureType;
    if(value.image === null) return;
    // create + bind texture to webgl
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // disable mipmaps + set clamping
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    // send image
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, value.image);
  }

  interpolate(left: any, right: any, t: number): any {
    return t < 0.5 ? left : right;
  }

  editComponent() { return TexturePicker; }

  alwaysConst() {
    // textures are always uniforms, so we display them with a locked const/uniform toggle
    return true;
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

// Camera control keys
const MOVE_LEFT = "a";
const MOVE_RIGHT = "d";
const MOVE_UP = "w";
const MOVE_DOWN = "s";
const MOVE_FORWARD = "q";
const MOVE_BACKWARD = "e";
const ROTATE_LEFT = "j";
const ROTATE_RIGHT = "l";
const ROTATE_UP = "i";
const ROTATE_DOWN = "k";
const ROTATE_ROLL_ANTICLOCKWISE = "u";
const ROTATE_ROLL_CLOCKWISE = "o";

const SCROLL_COEFF = 0.01;

// A 2d camera that can be moved around by the user
export type Camera2DPos = {
  center: Vec2, height: number, speed: number
}

class Property2DCamera extends Property {
  valFromGLSL(literal: string[]): any {
    const center = Property.vecFromGLSLLiteral(literal[0], 2);
    const height = Property.floatFromGLSLLiteral(literal[1]);
    return { center: center, height: height, speed: 1.0 };
  }

  toGLSLDecl(val: PropertyValue): string {
    const pos = val.value as Camera2DPos;
    let res = "";

    res += this.vars[0].toGLSLDecl(val.forceConst, Property.vecToGLSLLiteral(pos.center));
    res += this.vars[1].toGLSLDecl(val.forceConst, Property.floatToGLSLLiteral(pos.height));

    return res;
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst) return;

    const pos = val.value as Camera2DPos;
    if(!this.vars[0].isConst) {
      gl.uniform2fv(uniformLocs[this.vars[0].name], pos.center);
    }
    if(!this.vars[1].isConst) {
      gl.uniform1f(uniformLocs[this.vars[1].name], pos.height);
    }
  }

  interpolate(left: any, right: any, t: number): any {
    const pos0 = left as Camera2DPos;
    const pos1 = right as Camera2DPos;

    // If height doesn't change, linear movement
    if(pos0.height === pos1.height) {
      return {
        center: Property.interpolateVector(pos0.center, pos1.center, t),
        height: pos0.height,
        speed: Property.interpolateNumber(pos0.speed, pos1.speed, t),
      };
    }

    // Otherwise, interpolate on a base-2 exponential scale mapping to height
    // This produces what looks like a smooth zoom
    const exp_pos = Math.pow(2.0, Math.log2(pos1.height / pos0.height) * t);
    const height = pos0.height * exp_pos;
    const pos_t = pos0.height * (exp_pos - 1.0) / (pos1.height - pos0.height);
    const pos = Property.interpolateVector(pos0.center, pos1.center, pos_t);

    return {
      center: pos,
      height: height,
      speed: Property.interpolateNumber(pos0.speed, pos1.speed, t)
    };
  }

  editComponent() { return Camera2D; }
  usesCanvasEvents(): boolean { return true; }

  handleKeyPress(event: React.KeyboardEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera2DPos;
    let center = val.center;
    let height = val.height;
    // movement scale factors
    const speed = val.speed;
    const moveScale = 0.05 * speed;

    switch(event.key) {
      case MOVE_DOWN:
      case MOVE_UP: {
        const dir = (event.key === MOVE_DOWN ? 1 : -1);
        center[1] = center[1] + dir * height * moveScale;
        break;
      }
      case MOVE_RIGHT:
      case MOVE_LEFT: {
        const dir = (event.key === MOVE_RIGHT ? 1 : -1);
        center[0] = center[0] + dir * height * moveScale * aspectRatio;
        break;
      }
      case MOVE_FORWARD:
        height = height / (1 + speed/3);
        break;
      case MOVE_BACKWARD:
        height = height * (1 + speed/3);
        break;
    }
    return { forceConst: curVal.forceConst, value: { center: center, height: height, speed: speed }};
  }

  handleScroll(event: React.WheelEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera2DPos;
    // in order to keep zooming to appear constant, we use an exponential to adjust scroll (speed^scroll). This makes the factor that the screen size changes by be constant for the same wheel movement (ex: one mouse tick doubles or halves the screen size)
    let deltaMouse = event.deltaY;
    if(deltaMouse === 0) return curVal;

    const speedCoeff = 1 + val.speed / 3;
    const factor = Math.pow(speedCoeff, deltaMouse * SCROLL_COEFF);
    // determine the mouse location's coordinates
    const mouse = [
      (mousePos[0] - 0.5) * val.height * aspectRatio + val.center[0], 
      (mousePos[1] - 0.5) * val.height + val.center[1]
    ];
    return { forceConst: curVal.forceConst, value: {
      // zoom towards the mouse
      center: [
        val.center[0] * factor + mouse[0] * (1-factor),
        val.center[1] * factor + mouse[1] * (1-factor),
      ],
      height: val.height * factor,
      speed: val.speed,
    } };
  }

  handleMouseDrag(event: React.MouseEvent, curVal: PropertyValue, deltaMousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera2DPos;
    // convert delta from camera space
    const delta = [deltaMousePos[0] * val.height * aspectRatio, deltaMousePos[1] * val.height];
    const newCenter = [val.center[0] - delta[0], val.center[1] - delta[1]];

    return {
      forceConst: curVal.forceConst,
      value: { center: newCenter, height: val.height, speed: val.speed }
    };
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

// Utility -- Scalar vector multiply
function scalarMul(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

// Utility -- Vector add
function vecAdd(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

// Utility -- Cross product of two vectors
function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}

// Utility -- Dot product of two vectors
function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

// Utility -- rotate the vector v about k by theta
function rotateAbout(v: Vec3, k: Vec3, theta: number): Vec3 {
  // Rodrigues' rotation formula
  const el1 = scalarMul(v, Math.cos(theta));
  const el2 = scalarMul(cross(v, k), Math.sin(theta));
  const el3 = scalarMul(k, dot(k, v) * (1 - Math.cos(theta)));

  return vecAdd(el1, vecAdd(el2, el3));
}

// Utility -- Normalize vector
function normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);

  return scalarMul(v, 1.0 / len);
}

// A 3d camera that can be moved around by the user
export type Camera3DPos = {
  pos: Vec3;
  direction: Vec3;
  vup: Vec3;
  speed: number;
  dynamic: boolean;
};

class Property3DCamera extends Property {
  valFromGLSL(literal: string[]): any {
    const pos = Property.vecFromGLSLLiteral(literal[0], 3);
    const direction = Property.vecFromGLSLLiteral(literal[1], 3);
    const vup = Property.vecFromGLSLLiteral(literal[2], 3);
    return { pos: pos, direction: direction, vup: vup, speed: 1.0, dynamic: true };
  }

  toGLSLDecl(val: PropertyValue): string {
    const pos = val.value as Camera3DPos;
    const posComps = [pos.pos, pos.direction, pos.vup];
    let res = "";
    for(let i = 0; i < 3; i++) {
      res += this.vars[i].toGLSLDecl(val.forceConst, Property.vecToGLSLLiteral(posComps[i]));
    }

    return res;
  }

  setGLSLUniforms(val: PropertyValue, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    if(val.forceConst) return;

    const pos = val.value as Camera3DPos;
    const posComps = [pos.pos, pos.direction, pos.vup];
    for(let i = 0; i < 3; i++) {
      if(!this.vars[i].isConst) {
        gl.uniform3fv(uniformLocs[this.vars[i].name], posComps[i]);
      }
    }
  }

  interpolate(left: any, right: any, t: number): any {
    // TODO: rotate dir and vup rather than linear interpolation
    const pos0 = left as Camera3DPos;
    const pos1 = right as Camera3DPos;

    return {
      pos: Property.interpolateVector(pos0.pos, pos1.pos, t),
      direction: normalize(Property.interpolateVector(pos0.direction, pos1.direction, t) as Vec3),
      vup: normalize(Property.interpolateVector(pos0.vup, pos1.vup, t) as Vec3),
      speed: Property.interpolateNumber(pos0.speed, pos1.speed, t),
      dynamic: t < 0.5 ? pos0.dynamic : pos1.dynamic,
    };
  }

  editComponent() { return Camera3D; }

  usesCanvasEvents(): boolean { return true; }

  handleKeyPress(event: React.KeyboardEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera3DPos;
    let pos = val.pos;
    let dir = val.direction;
    let vup = val.vup;
    // movement scale factors
    const speed = val.speed;
    let moveSpeed = 0.05 * speed;
    const rotSpeed = 0.10 * speed;

    if(dist > 0.0 && val.dynamic) {
      moveSpeed *= dist * 5.0;
    }

    switch(event.key) {
      // movement
      case MOVE_LEFT:
      case MOVE_RIGHT: {
        const basis = normalize(cross(dir, vup));
        const moveDir = (event.key === MOVE_RIGHT ? 1 : -1);
        pos = vecAdd(pos, scalarMul(basis, moveSpeed * moveDir));
        break;
      }
      case MOVE_UP:
      case MOVE_DOWN: {
        const moveDir = (event.key === MOVE_DOWN ? 1 : -1);
        pos = vecAdd(pos, scalarMul(vup, moveSpeed * moveDir));
        break;
      }
      case MOVE_FORWARD:
      case MOVE_BACKWARD: {
        const moveDir = (event.key === MOVE_FORWARD ? 1 : -1);
        pos = vecAdd(pos, scalarMul(dir, moveSpeed * moveDir));
        break;
      }
      // rotation
      case ROTATE_LEFT:
      case ROTATE_RIGHT: {
        const rotDir = (event.key === ROTATE_RIGHT ? 1 : -1);
        // rotate dir about vup
        dir = normalize(rotateAbout(dir, vup, rotSpeed * rotDir));
        break;
      }
      case ROTATE_ROLL_ANTICLOCKWISE: 
      case ROTATE_ROLL_CLOCKWISE: {
        const rotDir = (event.key === ROTATE_ROLL_ANTICLOCKWISE ? 1 : -1);
        // rotate vup about dir
        vup = normalize(rotateAbout(vup, dir, rotSpeed * rotDir));
        break;
      }
      case ROTATE_UP:
      case ROTATE_DOWN: {
        const rotDir = (event.key === ROTATE_UP ? 1 : -1);
        const basis = normalize(cross(dir, vup));
        dir = normalize(rotateAbout(dir, basis, rotSpeed * rotDir));
        vup = normalize(rotateAbout(vup, basis, rotSpeed * rotDir));
        break;
      }
      default:
        break;
    }
    return { ...curVal, value: { ...val, pos: pos, direction: dir, vup: vup }};
  }

  handleMouseDrag(event: React.MouseEvent, curVal: PropertyValue, deltaMousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera3DPos;
    let dir = val.direction;
    let vup = val.vup;
    // mouse drag adjusts pitch + yaw rotation
    const rotSpeed = val.speed * 4.0;

    // apply mouse x movement (rotate dir about vup)
    dir = normalize(rotateAbout(dir, vup, deltaMousePos[0] * rotSpeed));
    // apply mouse y movement (rotate dir + vup)
    const basis = normalize(cross(dir, vup));
    dir = normalize(rotateAbout(dir, basis, -deltaMousePos[1] * rotSpeed));
    vup = normalize(rotateAbout(vup, basis, -deltaMousePos[1] * rotSpeed));

    return {
      ...curVal,
      value: { ...val, direction: dir, vup: vup }
    };
  }

  handleScroll(event: React.WheelEvent, curVal: PropertyValue, mousePos: Vec2, aspectRatio: number, dist: number): PropertyValue {
    const val = curVal.value as Camera3DPos;
    const speed = val.speed;
    const moveSpeed = SCROLL_COEFF * speed;

    let delta = event.deltaY * moveSpeed;
    if(dist > 0.0 && val.dynamic) {
      delta *= dist;
    }

    const newPos = vecAdd(val.pos, scalarMul(val.direction, delta));
    return { ...curVal, value: { ...val, pos: newPos } };
  }

  constructor(vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) {
    super();
    this.vars = vars;
    this.conditionalPropIndex = conditionalPropIndex;
  }
}

// A mapping from a $prop command in a shader to a Property instance
export interface PropertyType {
  // The kind of input method used for the property (slider, color, etc)
  kind: string;
  // The glsl types of the consts/uniforms backing the property
  type: GLSLType[];
  // Number of additional arguments to the $prop command
  values: number;
  // Property constructor for the type
  ctor: new (vars: GLSLProp[], args: string[], conditionalPropIndex: number | null) => Property;
}

// valid property types
export const propTypes: PropertyType[] = [
  // integer slider
  {
    kind: "slider",
    type: [new GLSLType("int", null)],
    values: 2,
    ctor: PropertyInt,
  },
  // integer input
  {
    kind: "input",
    type: [new GLSLType("int", null)],
    values: 0,
    ctor: PropertyInt,
  },
  // floating point sliders work on floats + vectors
  {
    kind: "slider",
    type: [new GLSLType("float", null)],
    values: 2,
    ctor: PropertyFloat,
  },
  {
    kind: "slider",
    type: [new GLSLType("vec2", null)],
    values: 2,
    ctor: PropertyFloat,
  },
  {
    kind: "slider",
    type: [new GLSLType("vec3", null)],
    values: 2,
    ctor: PropertyFloat,
  },
  {
    kind: "slider",
    type: [new GLSLType("vec4", null)],
    values: 2,
    ctor: PropertyFloat,
  },
  // floating point inputs
  {
    kind: "input",
    type: [new GLSLType("float", null)],
    values: 0,
    ctor: PropertyFloat,
  },
  {
    kind: "input",
    type: [new GLSLType("vec2", null)],
    values: 0,
    ctor: PropertyFloat,
  },
  {
    kind: "input",
    type: [new GLSLType("vec3", null)],
    values: 0,
    ctor: PropertyFloat,
  },
  {
    kind: "input",
    type: [new GLSLType("vec4", null)],
    values: 0,
    ctor: PropertyFloat,
  },
  // boolean checkbox
  {
    kind: "checkbox",
    type: [
      new GLSLType("bool", null),
    ],
    values: 0,
    ctor: PropertyCheckbox,
  },
  // color picker
  {
    kind: "color",
    type: [new GLSLType("vec3", null)],
    values: 0,
    ctor: PropertyColor,
  },
  // color gradient
  {
    kind: "color_palette",
    type: [
      new GLSLType("int", null),  // number of colors in the palette
      new GLSLType("vec3", "num_colors"),  // colors in the palette
      new GLSLType("float", "num_colors"), // offset of each color in the palette
    ],
    values: 0,
    ctor: PropertyColorPalette,
  },
  // movable 2d camera
  {
    kind: "camera_2d",
    type: [
      new GLSLType("vec2", null), // camera center
      new GLSLType("float", null) // camera height
    ],
    values: 0,
    ctor: Property2DCamera,
  },
  // movable 3d camera
  {
    kind: "camera_3d",
    type: [
      new GLSLType("vec3", null), // pos
      new GLSLType("vec3", null), // look direction
      new GLSLType("vec3", null)  // vertical up direction
    ],
    values: 0,
    ctor: Property3DCamera
  },
  // 2d image texture
  {
    kind: "texture",
    type: [
      new GLSLType("sampler2D", null),
    ],
    values: 0,
    ctor: PropertyTexture,
  }
];